import type { NavItem, ViewMode } from '@/types/layout';
import type { Role } from '@personal-trainer-app/shared/src/types/auth';

/**
 * Brand configuration for sidebar header
 */
export interface SidebarBrandConfig {
  /** Brand name (e.g., "Dō") */
  name: string;
  /** Brand tagline (e.g., "Personal Training") */
  tagline?: string;
}

/**
 * Section divider in sidebar navigation
 */
export interface SidebarSection {
  type: 'divider';
  id: string;
  /** Optional label for the section */
  label?: string;
}

/**
 * Sidebar navigation item with extended properties
 */
export interface SidebarNavItemData extends NavItem {
  /** Whether this item is active/selected */
  active?: boolean;
  /** Whether to expand nested items */
  expanded?: boolean;
}

/**
 * Union type for sidebar items (nav items or dividers)
 */
export type SidebarItem = SidebarNavItemData | SidebarSection;

/**
 * Check if item is a section divider
 */
export function isSidebarSection(item: SidebarItem): item is SidebarSection {
  return 'type' in item && item.type === 'divider';
}

/**
 * Props for Sidebar component
 */
export interface SidebarProps {
  /**
   * Navigation items to display
   */
  items: SidebarItem[];

  /**
   * Current active item ID
   */
  activeItemId?: string | undefined;

  /**
   * Called when a nav item is clicked
   */
  onItemClick?: (item: NavItem) => void;

  /**
   * Whether sidebar is in collapsed state
   * @default false
   */
  collapsed?: boolean;

  /**
   * User role to show appropriate navigation
   */
  userRole: Role | undefined;

  /**
   * Brand configuration for sidebar header
   * When provided, displays a brand header with logo and name
   */
  brand?: SidebarBrandConfig | undefined;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether to show the view mode dropup (only on dashboard)
   */
  showViewMode?: boolean;

  /**
   * Current view mode
   */
  viewMode?: ViewMode | undefined;

  /**
   * Called when view mode changes
   */
  onViewModeChange?: ((mode: ViewMode) => void) | undefined;
}

/**
 * Props for SidebarBrand component
 */
export interface SidebarBrandProps {
  /**
   * Brand configuration
   */
  brand: SidebarBrandConfig;

  /**
   * Whether sidebar is collapsed (shows only logo)
   */
  collapsed?: boolean;
}

/**
 * Props for SidebarNav component
 */
export interface SidebarNavProps {
  /**
   * Navigation items to display
   */
  items: SidebarItem[];

  /**
   * Current active item ID
   */
  activeItemId?: string | null;

  /**
   * Called when a nav item is clicked
   */
  onItemClick?: (item: NavItem) => void;

  /**
   * Whether in collapsed mode (icons only)
   */
  collapsed?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * Props for SidebarNavItem component
 */
export interface SidebarNavItemProps {
  /**
   * Navigation item data
   */
  item: SidebarNavItemData;

  /**
   * Whether this item is active/selected
   */
  active?: boolean;

  /**
   * Whether in collapsed mode (icon only)
   */
  collapsed?: boolean;

  /**
   * Nesting depth (0 = top level)
   */
  depth?: number;

  /**
   * Called when item is clicked
   */
  onClick?: (item: NavItem) => void;
}

/**
 * Props for SidebarDivider component
 */
export interface SidebarDividerProps {
  /**
   * Optional label for the section
   */
  label?: string;

  /**
   * Whether in collapsed mode
   */
  collapsed?: boolean;
}
